import React from 'react';

import { EShippingMethod } from '@/modules/cart/domain/Cart';
import { SHIPPING_METHOD_TYPE } from '@/modules/perseo-billing/constants/src';
import {
  DeliveryHeader,
  DeliveryIntangibleInfo,
  DeliveryNoviosInfo,
  DeliveryPickUpInfo,
  HomeDeliveryInfo,
  ScheduledDeferredInfo
} from '@/modules/perseo-billing/containers/payment-complete/components/purchase-detail/deliveries';
import { PurchaseItems } from '@/modules/perseo-billing/containers/payment-complete/components/purchase-detail/purchase-items.component';
import type {
  IBasketSummary,
  THomeDelivery,
  TIntangibleDelivery,
  TNoviosDelivery,
  TOrderConfirmationDelivery,
  TPickupDelivery
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing';
import { Typography } from '@/modules/perseo-billing/ui/react/src/components/beta';

function isHomeDelivery(
  delivery: TOrderConfirmationDelivery
): delivery is THomeDelivery {
  return delivery?.shippingMethodType === SHIPPING_METHOD_TYPE.HOME_DELIVERY;
}

function isPickUpDelivery(
  delivery: TOrderConfirmationDelivery
): delivery is TPickupDelivery {
  return delivery?.shippingMethodType === SHIPPING_METHOD_TYPE.PICK_UP;
}

function isIntangible(
  delivery: TOrderConfirmationDelivery
): delivery is TIntangibleDelivery {
  return delivery?.shippingMethodType === SHIPPING_METHOD_TYPE.INTANGIBLE;
}

function isNovios(
  delivery: TOrderConfirmationDelivery
): delivery is TNoviosDelivery {
  return delivery?.shippingMethodType === SHIPPING_METHOD_TYPE.NOVIOS;
}

function isScheduledDeferred(
  delivery: TOrderConfirmationDelivery
): delivery is THomeDelivery {
  return (
    isHomeDelivery(delivery) &&
    delivery?.shippingServiceName === EShippingMethod.SCHEDULED_DEFERRED
  );
}

type PurchaseProps = {
  basketSummary: IBasketSummary[];
};

export const Purchase = ({ basketSummary }: PurchaseProps) => {
  return (
    <div className="co-w-full co-flex co-flex-col co-rounded-8px co-gap-16px co-py-24px co-px-16px co-border-1 co-border-[#E6E6E6] lg:co-py-24px lg:co-px-32px co-bg-white">
      <div className="co-flex">
        <Typography
          size="h2"
          weight="semibold"
          className="co-text-pds-neutral-900"
          tabIndex={0}
        >
          Tu compra
        </Typography>
      </div>
      <div className="co-flex co-flex-col co-gap-16px">
        {basketSummary?.map((delivery: IBasketSummary, index: number) => (
          <Delivery key={index} delivery={delivery} index={index + 1} />
        ))}
      </div>
    </div>
  );
};

type TDeliveryDetails = {
  delivery: IBasketSummary;
  index: number;
};

const Delivery = ({ delivery, index }: TDeliveryDetails) => {
  if (isScheduledDeferred(delivery.deliveryDetails)) {
    return (
      <DeliveryDetail delivery={delivery} index={index}>
        <ScheduledDeferredInfo
          deliveryIcon="DeliveryBillingIcon"
          delivery={delivery.deliveryDetails}
        />
      </DeliveryDetail>
    );
  }

  if (isHomeDelivery(delivery.deliveryDetails)) {
    return (
      <DeliveryDetail delivery={delivery} index={index}>
        <HomeDeliveryInfo
          deliveryIcon="DeliveryBillingIcon"
          delivery={delivery.deliveryDetails}
        />
      </DeliveryDetail>
    );
  }

  if (isPickUpDelivery(delivery.deliveryDetails)) {
    return (
      <DeliveryDetail delivery={delivery} index={index}>
        <DeliveryPickUpInfo
          deliveryIcon="StoreBillingIcon"
          delivery={delivery.deliveryDetails}
        />
      </DeliveryDetail>
    );
  }

  if (isIntangible(delivery.deliveryDetails)) {
    return (
      <DeliveryDetail delivery={delivery} index={index}>
        <DeliveryIntangibleInfo
          deliveryIcon="EmailBillingIcon"
          delivery={delivery.deliveryDetails}
        />
      </DeliveryDetail>
    );
  }

  if (isNovios(delivery.deliveryDetails)) {
    return (
      <DeliveryDetail delivery={delivery} index={index}>
        <DeliveryNoviosInfo />
      </DeliveryDetail>
    );
  }

  return <></>;
};

type DeliveryDetailsProps = {
  delivery: IBasketSummary;
  index: number;
  children: React.ReactNode;
};

const DeliveryDetail = ({
  delivery,
  index,
  children
}: DeliveryDetailsProps) => {
  return (
    <div className="co-flex co-flex-col co-rounded-4px co-p-16px co-gap-16px co-border-1 co-border-[#E6E6E6] co-bg-white">
      <DeliveryHeader delivery={delivery} index={index} />
      <div className="co-flex co-flex-col lg:co-flex-row co-gap-16px">
        <PurchaseItems delivery={delivery} />
        <span className="co-w-1 co-flex-grow co-border-l-1 co-border-[#E6E6E6] "></span>
        <div className="co-flex co-flex-col lg:co-w-[350px] co-gap-8px co-flex-shrink-0">
          {children}
        </div>
      </div>
    </div>
  );
};
